import { Geografi, Parti, PartiValgResultat, ValgResultat, Valgtype } from '../domain'
import { getFromCache } from './cache'
import { request } from '../../lib/request'
import { delayReject } from '../../lib/delay'
import {
  joinPartivalgResultat,
  transformPartiValgResultat,
  transformValgResultat
} from './transform'

export type GrunndataResult = {
  parti: Parti[]
  geografi: Geografi[]
}

export class ElectionApi {
  baseUrl: string

  isClient: boolean

  constructor(year: string, url: string, isClient: boolean) {
    this.baseUrl = `${year}${url}`
    this.isClient = isClient
  }

  async get<T>(url: string, maxAge?: number): Promise<T> {
    return getFromCache<T>(
      url,
      () =>
        request(encodeURI(url)).catch(e => {
          if (__IS_SERVER__) {
            return Promise.reject(e)
          } else {
            // Dersom brukeren er offline returnerer request momentant.
            // Core-tabs håndterer ikke dette (har en timeout) og kræsjer.
            // Utsetter oppdatering slik at core-tabs får tid til å initialisere.
            return delayReject(20, new Error(`Kunne ikke laste ${url} Cause: ${e.message}`))
          }
        }),
      maxAge
    )
  }

  async getValgdata(geografi: Geografi, valg: Valgtype): Promise<ValgResultat> {
    const url = `${this.baseUrl}/${valg}/${geografi.key}`
    return this.get<ValgResultat>(url).then(transformValgResultat)
  }

  async getPartivalgdata(parti: Parti, valg: Valgtype): Promise<PartiValgResultat | ValgResultat> {
    const geografiType = valg === 'ko' ? 'kommune' : 'fylke'
    const valgData = parti.valg.find(v => v.valgKey === '2019/' + valg)
    const antallValgdistrikter = valgData ? valgData.antallValgdistrikter : 0

    if (antallValgdistrikter > 1) {
      const nasjonalUrl = `${this.baseUrl}/${valg}/parti/${parti.id}/Nasjon`
      const url = `${this.baseUrl}/${valg}/parti/${parti.id}/${geografiType}`

      return Promise.all([
        this.get<PartiValgResultat>(url),
        this.get<PartiValgResultat>(nasjonalUrl)
      ])
        .then(joinPartivalgResultat)
        .then(transformPartiValgResultat)
    } else if (antallValgdistrikter === 1 && valgData && valgData.geografiKey) {
      return this.get<ValgResultat>(`${this.baseUrl}/${valg}/${valgData.geografiKey}`).then(
        transformValgResultat
      )
    } else {
      throw new Error('Partivalgresultat inneholder ikke steder')
    }
  }

  async getGrunndataFor<T>(endpoint: 'geografi' | 'parti'): Promise<T> {
    return this.get<T>(`${this.baseUrl}/${endpoint}`, 60)
  }

  async getGrunndata(): Promise<GrunndataResult> {
    const [geografi, parti] = await Promise.all<Geografi[], Parti[]>([
      this.getGrunndataFor<Geografi[]>('geografi'),
      this.getGrunndataFor<Parti[]>('parti')
    ])

    return {
      geografi: geografi
        .filter(geografi => idFrom(geografi) !== '03010000')
        .map(geografi => {
          if (geografi.key === '') {
            geografi.navn.nn = 'Noreg'
            return geografi
          }

          return { ...geografi, stemmeberettigede: null }
        }),
      parti
    }
  }
}

export function idFrom(geografi: Geografi) {
  const [fylkesId, kommuneId, bydelOgKretsId] = geografi.key.split('/')
  switch (geografi.type) {
    case 'Nasjon':
      return ''
    case 'Fylke':
      return fylkesId
    case 'Kommune':
      return kommuneId
    case 'Bydel':
      return kommuneId + bydelOgKretsId.padStart(2, '0')
    case 'Krets':
      return kommuneId + bydelOgKretsId.padStart(4, '0')
  }
}
